import React, { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import {
  AppPicker,
  AppPickerAppEnum,
  Checkbox,
  MainMenu,
  Menu,
  MenuTop,
  useIsMobile,
} from '@bbl-digital/snorre-v2'
import { useAppSelector } from 'shared/store/store'
import { getMenu } from 'shared/store/menu/selectors'
import { getMenuIcon } from 'shared/utils/menu/menuIcon'
import MenuLoading from './MenuLoading'
import { getBbl, getBblDisplayNames, getBblSettings } from 'shared/store/bbl'
import { isActiveParent } from 'shared/utils/url'
import MobileContextMenu from 'App/Layout/shared/MobileContextMenu'
import { cn } from 'shared/utils/cn'
import { MenuItem } from './MenuItem'
import { isArendal, isBate, isBosor, isUsbl } from 'shared/utils/theme'
import { navigationBreakpoint } from 'shared/constants/styles'

interface IProps {
  open: boolean
  logo?: string
  onLogoClick?: () => void
  onClose?: () => void
}

const MenuLayout: React.FC<IProps> = ({ open, logo, onClose }) => {
  const { data, isLoading } = useAppSelector(getMenu)
  const { data: bbl } = useAppSelector(getBbl)
  const { navigateToLandingPageWhenBblLogoClickedInPortal } =
    useAppSelector(getBblSettings)
  const { myPageShort, portalShort } = useAppSelector(getBblDisplayNames)
  const location = useLocation()
  const small = !open
  const isMobile = useIsMobile(navigationBreakpoint)
  const mypageUrl = bbl?.settings?.urls?.myPage
  const usbl = isUsbl()
  const [keepExpanded, setKeepExpanded] = useState(() => {
    const stored = localStorage.getItem('sidebarKeepExpanded')
    return stored ? JSON.parse(stored) : false
  })

  useEffect(() => {
    localStorage.setItem('sidebarKeepExpanded', JSON.stringify(keepExpanded))
  }, [keepExpanded])

  const defaultExpanded = data?.find((item) =>
    isActiveParent(item.url, location.pathname)
  )?.url

  const [currentExpandedMenuUrl, setCurrentExpandedMenuUrl] = useState<
    string | null
  >(defaultExpanded)

  const hasDataAndNotLoading = data && !isLoading

  const MainMenuClasses = cn(
    '!w-full gap-1 py-1 !mt-1 !ml-0 overflow-y-scroll max-h-[calc(100vh-270px)] scrollbar-thin scrollbar-track-transparent scrollbar-thumb-neutral-0 box-border !px-[12px]',
    isMobile && 'max-h-[calc(100vh-220px)] '
  )

  return (
    <Menu open={open} className="relative overflow-y-auto md:fixed">
      {!isMobile && (
        <MenuTop
          small={small}
          logo={logo}
          logoHref={
            navigateToLandingPageWhenBblLogoClickedInPortal ? '/' : '/portal'
          }
        >
          <AppPicker
            variant={isBate() ? 'bate' : 'dark'}
            layout={usbl ? 'tiles' : 'dropdown'}
            small={small}
            withPortal={!usbl && small}
            selected={{ value: AppPickerAppEnum.Portalen }}
          >
            <AppPicker.App
              label={portalShort}
              value={AppPickerAppEnum.Portalen}
              href="/"
            />
            {mypageUrl && (
              <AppPicker.App
                label={myPageShort}
                value={AppPickerAppEnum.MinSide}
                href={mypageUrl}
              />
            )}
          </AppPicker>
        </MenuTop>
      )}

      <div
        className={cn(
          'flex flex-col justify-between w-full gap-1 menuwrapper',
          !isMobile && 'h-full'
        )}
        aria-label="Hovedmeny"
      >
        <MainMenu className={MainMenuClasses}>
          {isLoading && <MenuLoading />}
          {hasDataAndNotLoading &&
            data.map((item, index) => {
              return (
                !item.hidden && (
                  <MenuItem
                    item={item}
                    isExpanded={currentExpandedMenuUrl === item.url}
                    setCurrentExpandedMenuUrl={setCurrentExpandedMenuUrl}
                    small={small}
                    icon={getMenuIcon(
                      item.name,
                      isActiveParent(item.url, location.pathname)
                    )}
                    key={index}
                    active={isActiveParent(item.url, location.pathname)}
                    aria-current={isActiveParent(item.url, location.pathname)}
                  />
                )
              )
            })}
          {isMobile && <MobileContextMenu onClose={onClose} />}
        </MainMenu>
        {!isMobile && (
          <div className="flex flex-col items-center justify-center gap-1 pb-[44px] ">
            {open && (
              <Checkbox
                label="Hold meny åpen"
                wrapperClassName={cn(
                  'font-normal text-text-invert [&>div>label]:!font-normal  [&>div>label]:text-nowrap ',
                  (usbl || isBosor()) && '[&>div>input:checked]:!border-white',
                  (isArendal() || isBate()) &&
                    '[&>div>input:checked]:!border-white [&>div>label]:text-surface-portalen-aside-icon-active'
                )}
                checked={keepExpanded}
                onChange={(e) => setKeepExpanded(e.target.checked)}
                aria-label="Hold menyen åpen"
              />
            )}
          </div>
        )}
      </div>
    </Menu>
  )
}

export default MenuLayout
